import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useSeo, TESTIMONIALS } from "@/lib/site";
import { Reveal, MaskLines, ChapterHeading } from "@/components/Reveal";
import Marquee from "@/components/Marquee";

export default function About() {
  useSeo("About — Breeze Craft | Sydney Breeze Block Specialists", "Twenty years in the masonry trade. Licenced, insured and obsessed with straight walls and good shadow lines.");
  return (
    <div data-testid="about-page">
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-14 sm:py-20">
        <p className="kicker mb-4">About</p>
        <h1 className="font-display text-4xl sm:text-6xl font-medium tracking-tight text-ink" data-testid="about-heading">
          <MaskLines lines={["Masons first.", "Makers of", "good shadows."]} />
        </h1>
        <div className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-10">
          <Reveal delay={0.2}>
            <p className="text-neutral-600 leading-relaxed">
              Breeze Craft started on the tools in 2006 — bricklaying crews doing extensions and garden walls across the Eastern Suburbs.
              When breeze blocks came back into Sydney architecture, we went all in: sourcing the best Australian-cast profiles,
              holding stock, and building an install crew that treats every wall like it's going in a magazine.
            </p>
            <p className="mt-5 text-neutral-600 leading-relaxed">
              We resell rather than manufacture — which means our edge is service and install quality, not product lock-in.
              If a block exists in Australia, we can get it. If it goes on your wall, we lay it straight.
            </p>
          </Reveal>
          <Reveal delay={0.3} className="border border-ink/10 divide-y divide-ink/10">
            {[
              ["Est. 2006", "Twenty years in the masonry trade"],
              ["NSW Licenced", "Contractor licence 347441C"],
              ["Fully insured", "$20M public liability, workers comp"],
              ["Sydney-wide", "Supply and install across every region"],
            ].map(([t, d]) => (
              <div key={t} className="p-6 flex justify-between items-center gap-4">
                <span className="font-display text-lg font-medium text-ink">{t}</span>
                <span className="text-sm text-neutral-600 text-right">{d}</span>
              </div>
            ))}
          </Reveal>
        </div>
      </section>

      <Marquee items={["Straight walls", "Honest quotes", "On time", "Clean sites", "Engineer's spec", "Sydney owned"]} />

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-24">
        <ChapterHeading number="01" title="What clients say" />
        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-10">
          {TESTIMONIALS.map((t, i) => (
            <Reveal key={t.name} delay={i * 0.08}>
              <p className="text-lg text-ink leading-relaxed font-display">“{t.quote}”</p>
              <p className="mt-4 font-mono text-[11px] uppercase tracking-[0.2em] text-neutral-500">{t.name} · {t.role}</p>
            </Reveal>
          ))}
        </div>
        <Reveal className="mt-16">
          <Link to="/quote" data-testid="about-quote-button" className="inline-flex items-center gap-2 bg-terracotta text-white px-7 py-3.5 text-sm font-semibold hover:bg-terracotta-dark transition-colors">
            Work with us <ArrowRight size={16} />
          </Link>
        </Reveal>
      </section>
    </div>
  );
}
